// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from 'react';

/* ──────────────────────────────────────────────────────────────────────────
   Sales — document library for the sales/dev team.
   Marketing · Cost Models · Sales Presentations · Other.
   Simple upload-and-store per category, mirroring the HR Portal pattern.
   ────────────────────────────────────────────────────────────────────────── */

const _dark = { bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332", border: "#1E293B", borderLight: "#334155", text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B", accent: "#3B82F6", accentGlow: "rgba(59,130,246,0.15)", green: "#10B981", yellow: "#F59E0B", red: "#EF4444", purple: "#8B5CF6", orange: "#F97316", mode: "dark" };
const theme = new Proxy(_dark, { get: (fb, p) => { const t = (window as any).__ENGPORTAL_THEME; return t ? t[p] : fb[p]; } });

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'cost_models', label: 'Cost Models' },
  { key: 'sales_presentations', label: 'Sales Presentations' },
  { key: 'other', label: 'Other' },
];
const UPLOAD_CATS = CATEGORIES.filter(c => c.key !== 'all');
const CAT_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.key, c.label]));

const csrf = () => { const m = document.cookie.match(/csrftoken=([^;]+)/); return m ? m[1] : ''; };
const fmtDate = (iso) => { if (!iso) return '—'; const d = new Date(iso); return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); };
const fmtSize = (b) => { if (!b) return ''; const k = b / 1024; return k < 1024 ? `${k.toFixed(0)} KB` : `${(k / 1024).toFixed(1)} MB`; };

async function fetchAll(url) {
  let out = [], next = url, guard = 0;
  while (next && guard < 25) {
    const res = await fetch(next, { credentials: 'same-origin' });
    if (!res.ok) break;
    const data = await res.json();
    if (Array.isArray(data)) { out = out.concat(data); break; }
    out = out.concat(data.results || []); next = data.next; guard++;
  }
  return out;
}

export default function SalesPage({ currentUser }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');

  const [upCat, setUpCat] = useState('marketing');
  const [upTitle, setUpTitle] = useState('');
  const [upFile, setUpFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const input = { padding: '9px 12px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const btn = { padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: theme.green, color: '#fff', border: 'none', cursor: 'pointer' };

  const load = useCallback(async () => {
    setLoading(true);
    try { setDocs(await fetchAll('/api/sales/documents/')); } catch (e) { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab !== 'all') setUpCat(tab); }, [tab]);

  const counts = useMemo(() => {
    const c = {};
    for (const d of docs) c[d.category] = (c[d.category] || 0) + 1;
    return c;
  }, [docs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter(d => {
      if (tab !== 'all' && d.category !== tab) return false;
      if (q && !(`${d.system_doc_number} ${d.title}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [docs, tab, search]);

  const upload = async () => {
    if (!upFile) { setErr('Choose a file to upload.'); return; }
    setErr(''); setBusy(true);
    const fd = new FormData();
    fd.append('file', upFile);
    fd.append('category', upCat);
    fd.append('title', upTitle.trim() || upFile.name);
    try {
      const res = await fetch('/api/sales/documents/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'X-CSRFToken': csrf() }, body: fd,
      });
      if (!res.ok) { let m = 'Upload failed'; try { const j = await res.json(); m = j.detail || JSON.stringify(j); } catch (e) {} throw new Error(m); }
      setUpFile(null); setUpTitle('');
      const fi = document.getElementById('sales-file-input'); if (fi) (fi as any).value = '';
      await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      const res = await fetch(`/api/sales/documents/${id}/`, { method: 'DELETE', credentials: 'same-origin', headers: { 'X-CSRFToken': csrf() } });
      if (!res.ok && res.status !== 204) throw new Error('Delete failed');
      await load();
    } catch (e) { setErr(String(e.message || e)); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div>
        <h2 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>Sales</h2>
        <div style={{ color: theme.textDim, fontSize: 12, marginTop: 3 }}>Marketing, cost models, and sales presentations</div>
      </div>

      {/* Upload row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12 }}>
        <select style={{ ...input, flex: '0 0 auto' }} value={upCat} onChange={e => setUpCat(e.target.value)}>
          {UPLOAD_CATS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <input style={{ ...input, flex: 1, minWidth: 160 }} placeholder="Title (optional — defaults to filename)" value={upTitle} onChange={e => setUpTitle(e.target.value)} />
        <input id="sales-file-input" type="file" onChange={e => setUpFile(e.target.files[0])} style={{ color: theme.textMuted, fontSize: 12, flex: '1 1 200px' }} />
        <button style={btn} disabled={busy} onClick={upload}>{busy ? 'Uploading…' : 'Upload'}</button>
      </div>
      {err && <div style={{ background: theme.red + '18', color: theme.red, padding: '8px 12px', borderRadius: 8, fontSize: 12 }}>{err}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${theme.border}`, flexWrap: 'wrap' }}>
        {CATEGORIES.map(c => {
          const n = c.key === 'all' ? docs.length : (counts[c.key] || 0);
          return (
            <button key={c.key} onClick={() => setTab(c.key)} style={{
              padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent',
              border: 'none', borderBottom: tab === c.key ? `2px solid ${theme.accent}` : '2px solid transparent',
              color: tab === c.key ? theme.text : theme.textMuted, marginBottom: -1,
            }}>{c.label} <span style={{ color: theme.textDim, fontWeight: 400 }}>({n})</span></button>
          );
        })}
        <div style={{ flex: 1 }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" style={{ ...input, alignSelf: 'center', marginBottom: 4 }} />
      </div>

      {/* List */}
      <div style={{ flex: 1, overflow: 'auto', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textDim }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textDim, fontSize: 13 }}>No documents in this category yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bg, position: 'sticky', top: 0 }}>
                {['Doc #', 'Title', 'Category', 'Uploaded By', 'Date', 'Size', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: theme.textDim, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(d => (
                <tr key={d.id} style={{ borderBottom: `1px solid ${theme.border}` }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px 14px', color: theme.accent, fontWeight: 600, whiteSpace: 'nowrap' }}>{d.system_doc_number}</td>
                  <td style={{ padding: '10px 14px', color: theme.text }}>{d.title}</td>
                  <td style={{ padding: '10px 14px', color: theme.textMuted }}>{d.category_label || CAT_LABEL[d.category] || d.category}</td>
                  <td style={{ padding: '10px 14px', color: theme.textMuted, whiteSpace: 'nowrap' }}>{d.uploaded_by_name || '—'}</td>
                  <td style={{ padding: '10px 14px', color: theme.textDim, whiteSpace: 'nowrap' }}>{fmtDate(d.created_at)}</td>
                  <td style={{ padding: '10px 14px', color: theme.textDim, whiteSpace: 'nowrap' }}>{fmtSize(d.file_size)}</td>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <a href={`/api/sales/documents/${d.id}/view/`} target="_blank" rel="noopener noreferrer" style={{ color: theme.accent, textDecoration: 'none', marginRight: 12, fontSize: 12 }}>View</a>
                    <a href={`/api/sales/documents/${d.id}/download/`} download style={{ color: theme.accent, textDecoration: 'none', marginRight: 12, fontSize: 12 }}>Download</a>
                    <span onClick={() => remove(d.id)} style={{ color: theme.textDim, cursor: 'pointer', fontSize: 12 }} title="Delete">Delete</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
